<script setup lang="ts">
import { computed, ref } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue: string;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  }
);

const emits = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();
const visible = ref(false);

const selectColor = (color: string) => {
  emits("update:modelValue", color);
  visible.value = false;
};

const currentColor = computed(() => {
  return props.modelValue;
});

const contrastedColor = computed<"black" | "white">(() => {
  const hex = props.modelValue
    .replace("#", "")
    .match(/.{1,2}/g)
    ?.map((x) => parseInt(x, 16));
  if (!hex) return "black";
  const brightness = Math.round(
    (hex[0] * 299 + hex[1] * 587 + hex[2] * 114) / 1000
  );
  return brightness > 125 ? "black" : "white";
});
</script>

<template>
  <el-popover
    v-model:visible="visible"
    placement="bottom"
    :disabled="disabled"
    :width="240"
    trigger="click"
  >
    <template #reference>
      <slot
        name="reference"
        v-bind="{ currentColor, contrastedColor, disabled }"
      >
        <button
          :disable="disabled"
          :class="{ disabled }"
          class="main-button"
          :style="{ backgroundColor: currentColor }"
        >
          <i
            v-if="!props.disabled"
            class="bi bi-chevron-down"
            :style="{ color: contrastedColor }"
          ></i></button
      ></slot>
    </template>
    <slot></slot>
    <!-- bootstrap class to show children on same line, 1 line  -->
    <div class="p-0 m-0 d-flex flex-row flex-wrap">
      <div v-for="(colorLine, i) in colors" :key="i" class="d-flex flex-column">
        <button
          v-for="(color, j) in colorLine"
          :key="i + '-' + j"
          class="color-button"
          :style="{ backgroundColor: color }"
          @click="selectColor(color)"
        ></button>
      </div>
    </div>
  </el-popover>
</template>

<style lang="scss" scoped>
.main-button {
  outline: 1px solid #ccc;
  border: none;
  padding: 0;
  height: 20px;
  width: 20px;
  cursor: pointer;
  // center content
  display: flex;
  justify-content: center;
  align-items: center;
}
.color-button {
  outline: 1px solid #ccc;
  margin-top: 1px;
  margin-left: 1px;
  border-radius: 2px;
  border: none;
  padding: 0;
  height: 20px;
  width: 20px;
  cursor: pointer;
  // center content
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    border: 2px solid #000;
    transform: scale(1.1);
  }
}

.disabled {
  cursor: not-allowed !important;
}
</style>

<script lang="ts">
const colors = [
  // Rouge - du clair au foncé
  [
    "#ffe5e5",
    "#ffb3b3",
    "#ff8080",
    "#ff4d4d",
    "#ff1a1a",
    "#e60000",
    "#b30000",
    "#800000",
    "#4d0000",
    "#1a0000",
  ],
  // Orange
  [
    "#fff2e6",
    "#ffd9b3",
    "#ffbf80",
    "#ffa64d",
    "#ff8c1a",
    "#e67300",
    "#b35900",
    "#804000",
    "#4d2600",
    "#1a0d00",
  ],
  // Jaune
  [
    "#ffffe5",
    "#ffffb3",
    "#ffff80",
    "#ffff4d",
    "#ffff1a",
    "#e6e600",
    "#b3b300",
    "#808000",
    "#4d4d00",
    "#1a1a00",
  ],
  // Vert
  [
    "#e6ffe6",
    "#b3ffb3",
    "#80ff80",
    "#4dff4d",
    "#1aff1a",
    "#00e600",
    "#00b300",
    "#008000",
    "#004d00",
    "#001a00",
  ],
  // Turquoise / vert-bleu
  [
    "#e6fff5",
    "#b3ffe5",
    "#80ffd4",
    "#4dffc4",
    "#1affb3",
    "#00e699",
    "#00b377",
    "#008055",
    "#004d33",
    "#001a11",
  ],
  // Cyan
  [
    "#e6ffff",
    "#b3ffff",
    "#80ffff",
    "#4dffff",
    "#1affff",
    "#00e6e6",
    "#00b3b3",
    "#008080",
    "#004d4d",
    "#001a1a",
  ],
  // Bleu
  [
    "#e6f0ff",
    "#b3d1ff",
    "#80b3ff",
    "#4d94ff",
    "#1a75ff",
    "#005ce6",
    "#0047b3",
    "#003380",
    "#001f4d",
    "#000c1a",
  ],
  // Indigo / Bleu foncé
  [
    "#e6e6ff",
    "#b3b3ff",
    "#8080ff",
    "#4d4dff",
    "#1a1aff",
    "#0000e6",
    "#0000b3",
    "#000080",
    "#00004d",
    "#00001a",
  ],
  // Violet
  [
    "#f5e6ff",
    "#e0b3ff",
    "#cc80ff",
    "#b84dff",
    "#a31aff",
    "#8000e6",
    "#6600b3",
    "#4d0080",
    "#33004d",
    "#1a001a",
  ],
  // Rose / Magenta
  [
    "#ffe6f2",
    "#ffb3d9",
    "#ff80bf",
    "#ff4da6",
    "#ff1a8c",
    "#e60073",
    "#b30059",
    "#800040",
    "#4d0026",
    "#1a000d",
  ],
];
</script>
